<template>
  <div id="login">
    <div class="bg" />
    <div class="main">
      <div class="login-panel">
        <h1><logo-text uppercase /></h1>
        <h3>{{ $t('LOGIN') }}</h3>

        <form @submit="login">
          <select-input
            :items="branchOptions"
            :placeholder="$t('SELECT_BRANCH')"
            v-model="branch"
            ref="branchInput"
            required
          />
          <text-input
            :placeholder="$t('USERNAME')"
            v-model="username"
            required
            ref="usernameInput"
          />
          <text-input
            type="password"
            :placeholder="$t('PASSWORD')"
            v-model="password"
            required
            ref="passwordInput"
          />
          <primary-button :text="$t('LOG_IN')" @click="login" type="submit" />
        </form>

        <p class="copyright" v-html="$t('COPYRIGHT', { year })" />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from '@vue/composition-api';
import moment from 'moment';
import { createNamespacedHelpers } from 'vuex-composition-helpers';

import TextInput from '@/components/TextInput';
import PrimaryButton from '@/components/PrimaryButton';
import SelectInput from '@/components/SelectInput';
import LogoText from '@/components/LogoText';
import { PATHS, ERROR_CODES, PERMISSIONS } from '@/consts';
import { hasPermissions } from '@/helpers/permission';

const { useActions: useUserActions } = createNamespacedHelpers('user');
const { useActions: useFlashActions } = createNamespacedHelpers('flash');
const { useState: useAppState } = createNamespacedHelpers('app');

export default {
  name: 'Login',
  components: {
    SelectInput,
    TextInput,
    PrimaryButton,
    LogoText
  },
  metaInfo() {
    const { $i18n } = this;
    const title = $i18n.t('LOGIN');

    return {
      title,
      meta: []
    };
  },
  setup(props, { root }) {
    const username = ref('');
    const password = ref('');
    const branch = ref('CMT8');
    const usernameInput = ref(null);
    const passwordInput = ref(null);
    const branchInput = ref(null);

    const reset = () => {
      username.value = '';
      password.value = '';
    };

    const { login: userLogin } = useUserActions(['login']);
    const { error } = useFlashActions(['error']);
    const { branches } = useAppState(['branches']);

    const branchOptions = computed(() => branches.value.filter((b) => !['HO', 'LVL', 'GD'].includes(b.name)).map((b) => ({
      text: b.name,
      value: b.name
    })));

    const login = async (e) => {
      if (e) e.preventDefault();

      const inputs = [usernameInput.value, passwordInput.value, branchInput.value];

      let valid = true;

      inputs.forEach((input) => {
        input.validateInput();
        valid = valid && input.isValid;
      });

      if (valid) {
        try {
          await userLogin({ username: username.value, password: password.value, branch: branch.value });
          if (hasPermissions([PERMISSIONS.ORDER_ACCESS])) {
            root.$router.push({ path: PATHS.ORDERS });
          } else if (hasPermissions([PERMISSIONS.VALUE_ADJUSTMENT_ACCESS])) {
            root.$router.push({ path: PATHS.VALUE_ADJUSTMENT });
          } else if (hasPermissions([PERMISSIONS.PAYMENT_REMINDER_ACCESS])) {
            root.$router.push({ path: PATHS.PAYMENT_REMINDER });
          } else if (hasPermissions([PERMISSIONS.CASH_LOG_ACCESS])) {
            root.$router.push({ path: PATHS.CASH_LOG });
          } else {
            root.$router.push({ path: PATHS.ORDERS });
          }
        } catch(err) {
          const { code, message } = err;
          const loginErrorCodes = [ERROR_CODES.AUTH_USER_NOT_FOUND, ERROR_CODES.AUTH_INCORRECT_PASSWORD];
          const i = loginErrorCodes.findIndex((c) => c === code);

          if (i >= 0) {
            error(root.$t('LOGIN_ERROR'));
          } else {
            error(message || root.$t('LOGIN_ERROR'));
          }
        }
      }
    };

    return {
      year: moment().year(),
      username,
      password,
      branch,
      usernameInput,
      passwordInput,
      branchInput,
      branchOptions,
      login,
      reset
    };
  }
};
</script>

<style lang="scss">
@import "~@/assets/scss/colors";
@import "~@/assets/scss/dimens";

#login {
  height: 100vh;
  display: flex;
  position: relative;
  color: $independence;
  overflow: hidden;
  margin-left: -$sidePanelWidth;
  margin-top: -$headerHeight;

  @media (min-width: 991px) {
    width: 100vw;
  }

  @media (max-width: 991px) {
    margin-left: -$sidePanelWidthTablet;
  }

  .bg {
    background-image: url('~@/assets/imgs/login-bg.jpg');
    background-size: cover;
    position: absolute;
    top: -5px;
    left: -5px;
    width: calc(100% + 10px);
    height: calc(100% + 10px);
    filter: grayscale(0) blur(4px);
    z-index: -1;
  }

  .main {
    width: 100%;
    height: 100%;
    padding-left: $sidePanelWidth;

    @media (max-width: 991px) {
      padding-left: $sidePanelWidthTablet;
    }

    .login-panel {
      background-color: $babyPowder;
      border-radius: 28px;
      padding: 50px;
      max-width: 454px;
      position: relative;
      top: 50%;
      left: 50%;
      transform: translateX(-50%) translateY(-50%);
      text-align: center;

      h1 {
        font-size: 50px;
        margin-bottom: 0;
      }

      h3 {
        font-size: 30px;
        text-transform: uppercase;
        font-weight: bold;
        margin-bottom: 15px;
      }

      .input, .button {
        display: block;
        margin-bottom: 10px;
        width: 100%;
      }

      .input {
        text-align: left;
      }

      .copyright {
        font-style: italic;
        font-size: 14px;
      }

      @media (max-width: 767px) {
        max-width: 320px;
        padding: 30px;
        border-radius: 7px;

        h1 {
          font-size: 28px;
        }

        h3 {
          font-size: 18px;
        }

        .copyright {
          font-size: 12px;
        }
      }

      @media (max-width: 359px) {
        max-width: 300px;
      }
    }
  }
}
</style>
